import { useForm } from 'react-hook-form'

import { logisticApi } from 'redux/services/logistic'
import { CreateLogisticParams } from 'redux/services/logistic/types'

import { AppButton } from 'components/AppButton'
import AppCkeckbox from 'components/AppCkeckbox'
import { AppModal } from 'components/AppModal'
import { AppSelect } from 'components/AppSelect'
import { TextField } from 'components/TextField'
import { TextFieldCalendar } from 'components/TextFieldCalendar'
import { TextFieldPhone } from 'components/TextFieldPhone'

import { CART_TYPES } from 'constants/common'
import { CartType } from 'models/common'

import styles from './Create.module.scss'
import React from 'react'
import { values } from 'mobx'

interface Props {
  onClose: () => void
}
interface CreateLogisticForm extends Omit<CreateLogisticParams, 'coordinates'> {
  coordinates: string // ⚡️ строка вместо объекта
}

export function CreateLogisticModal({ onClose }: Props) {

  const {
    watch,
    register,
    clearErrors,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<CreateLogisticForm>()
  const { data: projects } = logisticApi.useGetProjectsQuery()
  const [submit, submitState] = logisticApi.useCreateLogisticMutation()

  function onSubmit(values: CreateLogisticForm) {
    let lon = 0
    let lat = 0

    if (values.coordinates) {
      const parts = values.coordinates.split(',').map(v => Number(v.trim()))
      lon = parts[0] || 0
      lat = parts[1] || 0
    }
    submit({
      ...values,
      coordinates: {
        type: 'Point',
        coordinates: [lon, lat],
      },
    })
      .unwrap()
      .then(() => {
        onClose()
      })
      .catch(err => {
        try {
          Object.entries(err.data.errors).forEach(([key, messages]) =>
            setError(key as keyof CreateLogisticParams, {
              type: 'custom',
              message: Array.isArray(messages) ? messages[0] : messages,
            }),
          )
        } catch (error) {
          console.error(error)
        }
      })
  }

  return (
    <AppModal onClose={onClose} title={`Создание заявки`}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
        <TextField
          {...register('client_full_name', {
            required: 'Обязательное поле',
          })}
          required
          label="ФИО клиента"
          withError
          error={errors.client_full_name}
        />

        <TextField
          {...register('address', {
            required: 'Обязательное поле',
          })}
          required
          label="Адрес"
          withError
          error={errors.address}
        />
        <TextField
          {...register('coordinates', {
            required: 'Обязательное поле',
            validate: value => {
              const [lon, lat] = value.split(',').map(v => Number(v.trim()))
              if (isNaN(lon) || isNaN(lat)) {
                return 'Введите координаты в формате: долгота, широта'
              }
              return true
            },
          })}
          required
          label="Долгота и широта (через запятую)"
          placeholder="77.59, 12.97"
          withError
          error={errors.coordinates}
        />
        <AppSelect
          label="Проект"
          text={projects?.find(p => p.id === watch('project'))?.name || ''}
          onChangeValues={values => {
            if (values[0]) {
              setValue('project', Number(values[0]))
              clearErrors('project')
            }
          }}
          list={
            projects?.map(item => ({
              value: item.id,
              title: item.name,
            })) || []
          }
          required
          withError
          error={errors.project}
        />

        <TextField
          type="number"
          {...register('cost', {
            required: 'Обязательное поле',
            // valueAsNumber: true,
          })}
          label="Стоимость доставки"
          min={0}
          required
          withError
          error={errors.cost}
        />

        <AppCkeckbox
          label="Оплачено"
          checked={watch('is_paid')}
          onChange={v => setValue('is_paid', v)}
        />
        <br />

        <div style={{ display: 'flex', gap: '1rem', paddingBottom: '2rem' }}>
          <div style={{ flex: 1 }}>
            <TextFieldCalendar
              required
              onChangeDate={date => {
                setValue(
                  'start_delivery',
                  `${date.date} ${date.hour}:${date.minute}`,
                )
                clearErrors('start_delivery')
              }}
              label="Время от"
              withError
              error={errors.start_delivery}
            />
          </div>

          <div style={{ flex: 1 }}>
            <TextField
              {...register('end_delivery')}
              type="time"
              required
              label="до"
              withError
              error={errors.end_delivery}
            />
          </div>
        </div>

        <TextFieldPhone
          onChangePhone={phone => {
            setValue('phone', phone.formattedPhone)
            clearErrors('phone')
          }}
          required
          value={watch('phone')}
          withError
          error={errors.phone}
        />

        <AppSelect
          label="Тип"
          text={watch('cart_type') ? CART_TYPES[watch('cart_type')] : ''}
          onChangeValues={values => {
            if (values[0]) {
              setValue('cart_type', values[0] as CartType)
              clearErrors('cart_type')
            }
          }}
          required
          list={Object.entries(CART_TYPES).map(([value, title]) => ({
            title,
            value,
          }))}
          withError
          error={errors.cart_type}
        />
        <TextField
          required
          value={watch('pan')}
          onChange={e => {
            setValue(
              'pan',
              e.currentTarget.value.replace(/\D/g, '').slice(0, 4),
            )
            clearErrors('pan')
          }}
          label="ПАН Карты"
          withError
          error={errors.pan}
        />

        <AppButton disabled={submitState.isLoading} type="submit">
          Создать
        </AppButton>
      </form>
    </AppModal>
  )
}
